<div class="w-full max-w-full relative space-y-6">
    <!-- Header -->
    <div class="py-2 px-1">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
                <h1 class="text-xl font-bold text-[#141437] dark:text-white">
                    Knowledge Management
                </h1>
                <p class="text-sm text-slate-500 dark:text-slate-400">
                    Efficiently manage your document collections and metadata.
                </p>
            </div>
            <div class="flex items-center gap-3">
                <app-button variant="default" (buttonClick)="openAddUserPanel()" [iconName]="'plus'"
                    className="flex items-center justify-center gap-2 shadow-[0px_6px_12px_-6px_#AD46FF] hover:shadow-[0px_4px_8px_-4px_#AD46FF] transition-all bg-[#AD46FF] hover:bg-[#AD46FF] border-none text-white">
                    <span class="font-medium whitespace-nowrap">Create Collection</span>
                </app-button>
            </div>
        </div>
    </div>

    <!-- Stats Section -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <!-- Total Collections -->
        <div
            class="bg-white dark:bg-surface-1 border border-slate-200 dark:border-slate-800 rounded-xl p-5 flex items-center gap-4">
            <div
                class="w-12 h-12 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                <app-icon name="folder" [size]="24"></app-icon>
            </div>
            <div>
                <p class="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total
                    Collections</p>
                <p class="text-2xl font-bold text-slate-900 dark:text-white">{{ totalCollections() }}</p>
            </div>
        </div>

        <!-- Total Documents -->
        <div
            class="bg-white dark:bg-surface-1 border border-slate-200 dark:border-slate-800 rounded-xl p-5 flex items-center gap-4">
            <div
                class="w-12 h-12 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center text-purple-600 dark:text-purple-400">
                <app-icon name="fileText" [size]="24"></app-icon>
            </div>
            <div>
                <p class="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total
                    Documents</p>
                <p class="text-2xl font-bold text-slate-900 dark:text-white">{{ totalDocuments() }}</p>
            </div>
        </div>

        <!-- Embedded -->
        <div
            class="bg-white dark:bg-surface-1 border border-slate-200 dark:border-slate-800 rounded-xl p-5 flex items-center gap-4">
            <div
                class="w-12 h-12 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                <app-icon name="check" [size]="24"></app-icon>
            </div>
            <div>
                <p class="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Embedded</p>
                <p class="text-2xl font-bold text-slate-900 dark:text-white">{{ embeddedDocuments() }}</p>
            </div>
        </div>

        <!-- Failed -->
        <div
            class="bg-white dark:bg-surface-1 border border-slate-200 dark:border-slate-800 rounded-xl p-5 flex items-center gap-4">
            <div
                class="w-12 h-12 rounded-lg bg-rose-50 dark:bg-rose-900/20 flex items-center justify-center text-rose-600 dark:text-rose-400">
                <app-icon name="x" [size]="24"></app-icon>
            </div>
            <div>
                <p class="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Failed</p>
                <p class="text-2xl font-bold text-slate-900 dark:text-white">{{ failedDocuments() }}</p>
            </div>
        </div>
    </div>

    <!-- Filter Section (Screenshot 1 Style) -->
    <!-- Filter Section (Screenshot 2 Style) -->
    <div class="bg-white dark:bg-surface-0 p-6 rounded-2xl border border-slate-200 dark:border-slate-800/60 shadow-sm">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6 items-end">
            <!-- Search -->
            <div class="lg:col-span-5 md:col-span-2">
                <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 ml-1">Search</label>
                <div class="relative">
                    <span class="absolute inset-y-0 left-3.5 flex items-center text-slate-400">
                        <app-icon name="search" [size]="18"></app-icon>
                    </span>
                    <input type="text" [ngModel]="collectionSearchQuery()"
                        (ngModelChange)="collectionSearchQuery.set($event)" (keyup.enter)="applyGlobalFilters()"
                        placeholder="Search collection..."
                        class="w-full pl-11 pr-4 py-2.5 bg-slate-50 dark:bg-surface-1 border border-slate-200 dark:border-slate-800 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500">
                </div>
            </div>

            <!-- Product -->
            <div class="lg:col-span-2 md:col-span-1">
                <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 ml-1">Product</label>
                <app-dropdown [options]="productOptions()" [selectedValue]="globalFilterProductId()"
                    (selectionChange)="onGlobalProductChange($event)" placeholder="Select Product" [fullWidth]="true"
                    variant="themeBase" [searchable]="false" />
            </div>

            <!-- Status -->
            <div class="lg:col-span-2 md:col-span-1">
                <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 ml-1">Status</label>
                <app-dropdown [options]="statusOptions" [selectedValue]="documentStatusFilter()"
                    (selectionChange)="documentStatusFilter.set($event)" placeholder="All Status" [fullWidth]="true"
                    variant="themeBase" [searchable]="false" />
            </div>

            <!-- Actions -->
            <div class="lg:col-span-3 md:col-span-2 flex items-center gap-3">
                <app-button variant="primary" (buttonClick)="applyGlobalFilters()"
                    className="flex-[1.5] h-[44px] bg-gradient-to-r from-[#AD46FF] to-[#8E2DE2] hover:opacity-90 text-white rounded-xl shadow-[0px_6px_15px_-4px_rgba(173,70,255,0.4)] transition-all font-semibold whitespace-nowrap">
                    Apply Filters
                </app-button>
                <app-button variant="ghost" (buttonClick)="clearGlobalFilters()"
                    className="flex-1 h-[44px] text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors font-medium whitespace-nowrap">
                    Clear Filters
                </app-button>
            </div>
        </div>
    </div>

    <!-- Main Content Area -->
    <div
        class="flex bg-white dark:bg-surface-1 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm h-[680px]">

        <!-- Left Sidebar: Collections -->
        <div class="w-80 flex flex-col border-r border-slate-100 dark:border-slate-800">
            <div class="p-4 border-b border-slate-100 dark:border-slate-800">
                <h2 class="text-sm font-bold text-slate-900 dark:text-white">Collections</h2>
            </div>

            <div class="flex-1 overflow-y-auto p-2 space-y-2">
                @if (processedCollectionData().length > 0) {
                @for (col of processedCollectionData(); track col.id) {
                <button (click)="selectCollection(col)" [ngClass]="{
                            'bg-purple-50/80 dark:bg-purple-900/10 border-[#AD46FF] border-2 shadow-sm': selectedCollection()?.id === col.id,
                            'border-[#EAEAEA] dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900/40': selectedCollection()?.id !== col.id
                        }" class="w-full text-left p-4 rounded-xl border transition-all group relative">
                    <div class="flex flex-col mb-3">
                        <h3 class="text-sm font-bold truncate pr-3 mb-0.5" [ngClass]="{
                                    'text-purple-700 dark:text-purple-400': selectedCollection()?.id === col.id,
                                    'text-slate-900 dark:text-slate-100': selectedCollection()?.id !== col.id
                                }">
                            {{ col.name || col.collection_name }}
                        </h3>
                        <span class="text-[10px] text-slate-400">Created on {{ col.created_at | date:'MMM d, y'
                            }}</span>
                    </div>

                    <div class="space-y-1">
                        <div class="flex items-center gap-1.5 overflow-hidden">
                            <span
                                class="text-[10px] font-bold text-slate-400 uppercase tracking-tighter shrink-0">Product:</span>
                            <span class="text-[11px] text-slate-600 dark:text-slate-400 truncate">{{
                                col.product_name }}</span>
                        </div>
                        <div class="flex items-center gap-1.5 overflow-hidden">
                            <span
                                class="text-[10px] font-bold text-slate-400 uppercase tracking-tighter shrink-0">Feature:</span>
                            <span class="text-[11px] text-slate-600 dark:text-slate-400 truncate">{{
                                col.feature_name }}</span>
                        </div>
                        @if (col.option_name) {
                        <div class="flex items-center gap-1.5 overflow-hidden">
                            <span
                                class="text-[10px] font-bold text-slate-400 uppercase tracking-tighter shrink-0">Option:</span>
                            <span class="text-[11px] text-slate-600 dark:text-slate-400 truncate">{{ col.option_name
                                }}</span>
                        </div>
                        }
                    </div>

                    <!-- Name Copy Box and Doc Count -->
                    <div class="mt-3 bg-white dark:bg-slate-900/40 border border-[#EAEAEA] dark:border-slate-800 rounded-lg p-2 px-3 flex items-center justify-between group/copy"
                        (click)="$event.stopPropagation()">
                        <span
                            class="text-[11px] font-medium text-[#AD46FF] dark:text-purple-400 truncate mr-2 select-all">{{
                            col.collection_name }}</span>
                        <button (click)="copyToClipboard(col.collection_name)" class="shrink-0 p-0.5"
                            aria-label="Copy Name">
                            <app-icon name="copy" [size]="14"
                                class="text-slate-400 group-hover/copy:text-[#AD46FF] transition-colors cursor-pointer"></app-icon>
                        </button>
                    </div>

                    <div class="mt-2">
                        <span class="text-[11px] text-slate-500 dark:text-slate-400">{{ col.document_count || 0
                            }} documents</span>
                    </div>
                </button>
                }
                } @else {
                <div class="h-full flex flex-col items-center justify-center text-center p-6 py-12">
                    <div
                        class="w-12 h-12 bg-slate-50 dark:bg-slate-900/40 rounded-full flex items-center justify-center text-slate-300 dark:text-slate-700 mb-3">
                        <app-icon name="folder" [size]="24"></app-icon>
                    </div>
                    <h3 class="text-sm font-bold text-slate-900 dark:text-white mb-1">No collections found</h3>
                    <p class="text-[11px] text-slate-500 dark:text-slate-400 max-w-[180px] mx-auto">
                        No collections match your current filters. Try adjusting your search or filters.
                    </p>
                </div>
                }
            </div>
        </div>

        <!-- Right Content: Documents Table -->
        <div class="flex-1 min-w-0 flex flex-col">
            <!-- Table Header/Filters -->
            <div
                class="p-4 border-b border-slate-100 dark:border-slate-800 flex flex-wrap items-center justify-between gap-4">
                <div class="flex items-center gap-4">
                    <h2 class="text-sm font-bold text-slate-900 dark:text-white">Documents</h2>
                    <div class="h-4 w-[1px] bg-slate-200 dark:bg-slate-700"></div>
                    <p class="text-xs text-slate-500 dark:text-slate-400">Collection: <span
                            class="font-bold text-slate-700 dark:text-slate-300">{{
                            selectedCollection()?.collection_name || 'All' }}</span></p>

                    <!-- Active / Archived toggle -->
                    <div class="inline-flex items-center rounded-lg bg-slate-100 dark:bg-slate-800/60 p-0.5">
                        <button type="button" (click)="setDocumentView('active')" [ngClass]="documentView() === 'active'
                                ? 'bg-white dark:bg-surface-1 text-purple-700 dark:text-purple-300 shadow-sm'
                                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'"
                            class="px-3 py-1 rounded-md text-xs font-semibold transition-all">
                            Active
                        </button>
                        <button type="button" (click)="setDocumentView('archived')" [ngClass]="documentView() === 'archived'
                                ? 'bg-white dark:bg-surface-1 text-amber-600 dark:text-amber-400 shadow-sm'
                                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'"
                            class="px-3 py-1 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5">
                            <app-icon name="archive" [size]="12"></app-icon>
                            Archived
                        </button>
                    </div>
                </div>
                <div class="flex flex-wrap items-center gap-3 flex-1 justify-start lg:justify-end min-w-0">
                    <button (click)="openQueryPanel()"
                        class="gradient-border text-[#141437] dark:text-white text-xs font-semibold px-4 py-2 rounded-lg transition-all duration-200 shadow-[0px_6px_12px_-6px_#AD46FF] hover:shadow-[0px_7px_12px_-5px_#AD46FF] flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap h-9">
                        <app-icon name="sparkles" [size]="14" />
                        Ask Your Query
                    </button>
                    <div class="relative flex-1 md:flex-none md:min-w-[200px]">
                        <span class="absolute inset-y-0 left-3 flex items-center text-slate-400">
                            <app-icon name="search" [size]="14"></app-icon>
                        </span>
                        <input type="text" [ngModel]="documentSearchQuery()"
                            (ngModelChange)="documentSearchQuery.set($event)" (keyup.enter)="applyGlobalFilters()"
                            placeholder="Search documents..."
                            class="w-full pl-9 pr-4 h-9 bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all">
                    </div>

                    <app-button variant="outline" size="sm" (buttonClick)="openAddDocumentPanel()" [iconName]="'upload'"
                        className="flex items-center justify-center gap-2 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 h-9">
                        <span class="text-xs font-semibold whitespace-nowrap">Upload Document</span>
                    </app-button>

                    <!-- Environment selector: where the selected collection is visible (for_env) -->
                    <div class="relative h-9" title="Environment this collection is visible in — pick another to move it">
                        <app-dropdown [options]="envOptions" [selectedValue]="envDraft()"
                            (selectionChange)="requestEnvChange($event)" placeholder="Environment"
                            [disabled]="!selectedCollection()" variant="themeBase" size="sm" [searchable]="false"
                            [customWidth]="'220px'" textClass="text-xs font-semibold text-slate-800 dark:text-slate-100"
                            [triggerIconSize]="14" [wrapLabels]="false" [showTooltip]="false" />
                    </div>
                </div>
            </div>

            <!-- Table Content -->
            <div class="flex-1 overflow-y-auto p-2 flex flex-col gap-2">
                <!-- Archived view notice -->
                @if (documentView() === 'archived') {
                <div
                    class="flex items-start gap-2.5 px-3.5 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-800/40 text-amber-700 dark:text-amber-400">
                    <app-icon name="archive" [size]="16" class="mt-0.5 shrink-0"></app-icon>
                    <p class="text-xs leading-relaxed">
                        <span class="font-semibold">Archived documents are hidden from search and active use,</span>
                        but nothing is permanently deleted. Use the <span class="font-semibold">Restore</span>
                        action to bring a document back to the active list.
                    </p>
                </div>
                }

                @if (isLoading()) {
                <div class="h-full flex flex-col items-center justify-center gap-3">
                    <div class="w-8 h-8 border-3 border-purple-600 border-t-transparent rounded-full animate-spin">
                    </div>
                    <span class="text-slate-500 dark:text-slate-400 text-sm font-medium">Fetching
                        documents...</span>
                </div>
                } @else if (processedDocumentData().length > 0) {
                <app-paginated-table style="display: flex; flex-direction: column; flex: 1; min-height: 0;"
                    (actionSelected)="handleTableAction($event)" [tableData]="processedDocumentData()"
                    [headers]="documentTableHeaders" [isCheckboxShow]="false"
                    [totalItems]="processedDocumentData().length" [pageSize]="docPageSize()" [currentPage]="docPage()"
                    (pageChange)="onDocumentPageChange($event)" [serverSide]="false"
                    [tableActionOptions]="{showToggleButton: true, toggleOptions:currentActionOptions(), columnLabel: 'Actions'}"
                    tableWrapperClass="flex-1 overflow-hidden rounded-2xl dark:bg-surface-1 bg-white backdrop-blur-xl" />
                } @else {
                <div class="h-full flex flex-col items-center justify-center text-center p-12">
                    <div
                        class="w-16 h-16 bg-slate-50 dark:bg-slate-900/40 rounded-full flex items-center justify-center text-slate-300 dark:text-slate-700 mb-4">
                        <app-icon [name]="documentView() === 'archived' ? 'archive' : 'fileText'" [size]="32"></app-icon>
                    </div>
                    @if (documentView() === 'archived') {
                    <h3 class="text-base font-bold text-slate-900 dark:text-white mb-1">No archived documents</h3>
                    <p class="text-xs text-slate-500 dark:text-slate-400 max-w-xs mx-auto">
                        Nothing has been archived in this collection. Archived documents can be restored, so removals
                        are never permanent.
                    </p>
                    } @else {
                    <h3 class="text-base font-bold text-slate-900 dark:text-white mb-1">No documents found</h3>
                    <p class="text-xs text-slate-500 dark:text-slate-400 max-w-xs mx-auto">
                        There are no documents in this collection yet. Click the upload button to add your first
                        document.
                    </p>
                    }
                </div>
                }
            </div>
        </div>
    </div>

    <!-- Slide Panel: Add Collection -->
    <app-slide-panel [open]="panelOpen()" [title]="'Create Collection'" (closed)="closePanel()"
        panelClass="min-w-[600px] lg:min-w-[700px] max-w-[800px] h-full flex flex-col overflow-hidden card-bg border-l border-[#EAEAEA] dark:border-line">
        <form [formGroup]="form" (ngSubmit)="submitForm()" class="flex flex-col flex-1 overflow-hidden h-full">
            <section class="flex flex-col gap-6 flex-1 overflow-y-auto p-8 text-[#141437] dark:text-white">
                <div class="space-y-4">
                    <p class="text-sm text-slate-500 dark:text-slate-400 border-l-2 border-purple-500 pl-4 py-1">
                        Select context for your collection to categorize information effectively.
                    </p>
                </div>

                <!-- Collection Name -->
                <div class="space-y-2">
                    <label class="text-xs font-bold text-slate-500 uppercase tracking-wider block">Collection Name <span
                            class="text-rose-500">*</span></label>
                    <app-input formControlName="collection_name" placeholder="Enter collection name"
                        [error]="getErrorMessage('collection_name')"></app-input>
                </div>


                <!-- Product Selection -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div class="space-y-2">
                        <label class="text-xs font-bold text-slate-500 uppercase tracking-wider block">Product</label>
                        <app-dropdown [options]="productOptions()" formControlName="product_id"
                            placeholder="Select product" variant="themeBase" size="lg" [searchable]="false"
                            (selectionChange)="onProductChange($event)"></app-dropdown>
                    </div>

                    <!-- Feature Selection -->
                    <div class="space-y-2">
                        <label class="text-xs font-bold text-slate-500 uppercase tracking-wider block">Feature</label>
                        <app-dropdown [options]="featureOptions()" formControlName="product_feature_id"
                            variant="themeBase" size="lg" placeholder="Select feature" [searchable]="false"
                            [disabled]="!form.get('product_id')?.value"
                            (selectionChange)="onFeatureChange($event)"></app-dropdown>
                    </div>
                </div>

                @if (selectedFeature() === 'addNewFeature') {
                <div class="space-y-2">
                    <label class="text-xs font-bold text-slate-500 uppercase tracking-wider block">New Feature
                        Name</label>
                    <app-input formControlName="featureName" placeholder="e.g. Authentication"
                        [error]="getErrorMessage('featureName')"></app-input>
                </div>
                }

                <!-- Option Selection -->
                <div class="space-y-2">
                    <label class="text-xs font-bold text-slate-500 uppercase tracking-wider block">Option</label>
                    <app-dropdown [options]="featureOptionList()" formControlName="feature_option_id"
                        variant="themeBase" size="lg" placeholder="Select option" [searchable]="false"
                        [disabled]="!form.get('product_feature_id')?.value"
                        (selectionChange)="onOptionChange($event)"></app-dropdown>
                </div>

                @if (selectedOption() === 'addNewOption') {
                <div class="space-y-2">
                    <label class="text-xs font-bold text-slate-500 uppercase tracking-wider block">New Option
                        Name</label>
                    <app-input formControlName="optionName" placeholder="e.g. JWT Login"
                        [error]="getErrorMessage('optionName')"></app-input>
                </div>
                }

                <!-- Description -->
                <div class="space-y-2">
                    <label class="text-xs font-bold text-slate-500 uppercase tracking-wider block">Description <span
                            class="text-slate-400 font-normal normal-case">(optional)</span></label>
                    <textarea formControlName="description" placeholder="Brief description of this collection..."
                        rows="3"
                        class="w-full px-4 py-2.5 bg-white dark:bg-surface-1 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20 resize-none transition-all"></textarea>
                </div>
            </section>

            <!-- Footer Action -->
            <div
                class="p-8 sticky bottom-0 bg-white dark:bg-surface-1 border-t border-[#EAEAEA] dark:border-line mt-auto">
                <div class="flex items-center justify-end gap-3">
                    <app-button variant="outline" (buttonClick)="closePanel()" className="px-6">Cancel</app-button>
                    <app-button type="submit" variant="default"
                        className="px-10 shadow-lg shadow-purple-500/20 bg-purple-600 border-none hover:bg-purple-700 text-white"
                        [disabled]="isLoading() || form.invalid">
                        <span class="text-sm font-bold">{{ isLoading() ? 'Saving...' : 'Create Collection' }}</span>
                    </app-button>
                </div>
            </div>
        </form>
    </app-slide-panel>

    <!-- Slide Panel: Upload Document -->
    <app-slide-panel [open]="documentPanelOpen()" [title]="'Upload Document'" (closed)="closeDocumentPanel()"
        panelClass="min-w-[600px] lg:min-w-[700px] max-w-[800px] h-full flex flex-col overflow-hidden card-bg border-l border-[#EAEAEA] dark:border-line">
        <form [formGroup]="documentForm" (ngSubmit)="submitDocumentForm()"
            class="flex flex-col flex-1 overflow-hidden h-full">
            <section class="flex flex-col gap-6 flex-1 overflow-y-auto p-8 text-[#141437] dark:text-white">
                <div class="space-y-2">
                    <label class="text-xs font-bold text-slate-500 uppercase tracking-wider block">Target
                        Collection</label>
                    <app-dropdown [options]="collectionOptions()" formControlName="collection_id"
                        placeholder="Select collection" variant="themeBase" size="lg"
                        [searchable]="false"></app-dropdown>

                    @if (selectedUploadCollectionDetails; as col) {
                    <div
                        class="mt-2 space-y-1.5 p-3.5 bg-slate-50 dark:bg-white/5 rounded-2xl border border-slate-100 dark:border-white/10 animate-fade-in">
                        <div class="flex items-center gap-2">
                            <span
                                class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider shrink-0 w-16">Product:</span>
                            <span class="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">{{
                                col.product_name }}</span>
                        </div>
                        <div class="flex items-center gap-2">
                            <span
                                class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider shrink-0 w-16">Feature:</span>
                            <span class="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">{{
                                col.feature_name }}</span>
                        </div>
                        @if (col.option_name) {
                        <div class="flex items-center gap-2">
                            <span
                                class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider shrink-0 w-16">Option:</span>
                            <span class="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">{{
                                col.option_name }}</span>
                        </div>
                        }
                    </div>
                    }

                    @if (getErrorMessage('collection_id')) {
                    <p class="text-xs text-rose-500 mt-1">{{ getErrorMessage('collection_id') }}</p>
                    }
                </div>

                <div class="space-y-4">
                    <label class="text-xs font-bold text-slate-500 uppercase tracking-wider block">Upload File</label>
                    <div class="relative group cursor-pointer">
                        <input type="file" (change)="onFileSelected($event)"
                            class="absolute inset-0 w-full h-full opacity-0 z-10 cursor-pointer"
                            accept=".pdf,.docx,.txt" />
                        <div
                            [ngClass]="selectedFile
                                ? 'border-2 border-dashed border-emerald-400 dark:border-emerald-700 bg-emerald-50/5 dark:bg-emerald-900/5 rounded-2xl p-12 text-center transition-all'
                                : 'border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl p-12 text-center group-hover:border-purple-500/50 group-hover:bg-purple-50/5 dark:group-hover:bg-white/5 transition-all'">
                            @if (!selectedFile) {
                            <div
                                class="w-16 h-16 bg-purple-50 dark:bg-purple-900/20 rounded-full flex items-center justify-center text-purple-600 dark:text-purple-400 mx-auto mb-4 group-hover:scale-110 transition-transform">
                                <app-icon name="upload" [size]="32"></app-icon>
                            </div>
                            } @else {
                            <div
                                class="w-16 h-16 bg-emerald-50 dark:bg-emerald-900/20 rounded-full flex items-center justify-center text-emerald-600 dark:text-emerald-400 mx-auto mb-4">
                                <app-icon name="fileText" [size]="32"></app-icon>
                            </div>
                            }
                            <h4 class="text-lg font-bold text-slate-900 dark:text-white mb-2">
                                {{ selectedFile ? selectedFile.name : 'Select or drop file here' }}
                            </h4>
                            @if (!selectedFile) {
                            <p class="text-sm text-slate-500 dark:text-slate-400">
                                Supports PDF, DOCX, and TXT (Max 10MB)
                            </p>
                            } @else {
                            <!-- <button type="button" (click)="$event.stopPropagation(); removeFile()"
                                class="text-xs font-semibold text-rose-500 hover:text-rose-600 transition-colors mt-1">
                                Remove file
                            </button> -->
                            }
                        </div>
                    </div>
                    @if (formSubmitted() && !selectedFile) {
                    <p class="text-xs text-rose-500">Please select a file to upload</p>
                    }
                </div>

                <!-- Description -->
                <div class="space-y-2">
                    <label class="text-xs font-bold text-slate-500 uppercase tracking-wider block">Description <span
                            class="text-slate-400 font-normal normal-case">(optional)</span></label>
                    <textarea formControlName="description" placeholder="Brief description of this document..." rows="3"
                        class="w-full px-4 py-2.5 bg-white dark:bg-surface-1 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20 resize-none transition-all"></textarea>
                </div>
            </section>

            <!-- Footer Action -->
            <div
                class="p-8 sticky bottom-0 bg-white dark:bg-surface-1 border-t border-[#EAEAEA] dark:border-line mt-auto">
                <div class="flex items-center justify-end gap-3">
                    <app-button variant="outline" (buttonClick)="closeDocumentPanel()"
                        className="px-6">Cancel</app-button>
                    <app-button type="submit" variant="default"
                        className="px-10 shadow-lg shadow-purple-500/20 bg-purple-600 border-none hover:bg-purple-700 text-white"
                        [disabled]="isLoading() || documentForm.invalid">
                        <span class="text-sm font-bold">{{ isLoading() ? 'Uploading...' : 'Upload' }}</span>
                    </app-button>
                </div>
            </div>
        </form>
    </app-slide-panel>

    <!-- Slide Panel: Query Knowledge Base -->
    <app-slide-panel [open]="queryPanelOpen()" [title]="'Query Knowledge Base'" (closed)="closeQueryPanel()"
        panelClass="min-w-[600px] lg:min-w-[700px] max-w-[800px] h-full flex flex-col overflow-hidden card-bg border-l border-[#EAEAEA] dark:border-line">
        <div class="flex flex-col h-full overflow-hidden">

            <!-- Collection selector bar -->
            <div class="px-6 py-3 border-b border-slate-100 dark:border-slate-800 flex flex-col gap-1.5 shrink-0">
                <div class="flex items-center gap-3">
                    <span class="text-xs font-bold text-slate-400 uppercase tracking-wider shrink-0">Collection</span>
                    <div class="flex-1">
                        <app-dropdown [options]="collectionOptions()" [selectedValue]="queryCollectionName()"
                            (selectionChange)="onQueryCollectionChange($event)" placeholder="Select a collection"
                            variant="themeBase" [searchable]="false" [fullWidth]="true"
                            [disabled]="isQueryCollectionFrozen()"></app-dropdown>
                    </div>
                </div>

                <!-- Collection details box (Single Line) -->
                @if (selectedQueryCollectionDetails; as col) {
                <div
                    class="mt-1 px-3 py-2 bg-slate-50 dark:bg-white/5 rounded-2xl border border-slate-100 dark:border-white/10 animate-fade-in flex flex-wrap items-center gap-x-4 gap-y-1">
                    <div class="flex items-center gap-1.5">
                        <span
                            class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider shrink-0">Product:</span>
                        <span class="text-xs font-semibold text-slate-700 dark:text-slate-200">{{ col.product_name
                            }}</span>
                    </div>
                    <div class="w-[1px] h-3 bg-slate-200 dark:bg-slate-700 hidden sm:block"></div>
                    <div class="flex items-center gap-1.5">
                        <span
                            class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider shrink-0">Feature:</span>
                        <span class="text-xs font-semibold text-slate-700 dark:text-slate-200">{{ col.feature_name
                            }}</span>
                    </div>
                    @if (col.option_name) {
                    <div class="w-[1px] h-3 bg-slate-200 dark:bg-slate-700 hidden sm:block"></div>
                    <div class="flex items-center gap-1.5">
                        <span
                            class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider shrink-0">Option:</span>
                        <span class="text-xs font-semibold text-slate-700 dark:text-slate-200">{{ col.option_name
                            }}</span>
                    </div>
                    }
                </div>
                }
            </div>

            <!-- Chat Messages Area -->
            <div #chatScrollArea class="flex-1 min-h-0 overflow-y-auto px-6 py-4 flex flex-col gap-4">

                <!-- Date badge -->
                <div class="flex justify-center">
                    <span
                        class="px-4 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-xs text-slate-500 dark:text-slate-400 font-medium">Today</span>
                </div>

                <!-- Empty / Welcome state (no messages yet) -->
                @if (chatHistory().length === 0) {
                <div class="flex flex-col items-center justify-center text-center py-10 gap-4">
                    <div class="w-14 h-14 rounded-full bg-purple-600 flex items-center justify-center">
                        <app-icon name="sparkles" [size]="28" class="text-white"></app-icon>
                    </div>
                    <div>
                        <h3 class="text-sm font-semibold text-slate-800 dark:text-white">RAG Assistant</h3>
                        <p class="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xs">
                            Hello! How can I assist you with your knowledge base today?
                        </p>
                    </div>

                </div>
                }
                <!-- Chat message list -->
                @for (msg of chatHistory(); track $index) {
                @if (msg.role === 'user') {
                <!-- User message bubble -->
                <div class="flex flex-col items-end gap-1.5 animate-in slide-in-from-right-4 duration-300">
                    <div class="flex items-center gap-2 px-1">
                        <span class="text-[10px] font-bold text-slate-400 uppercase tracking-tight">{{ msg.time
                            }}</span>
                        <span class="text-xs font-bold text-slate-600 dark:text-slate-300">You</span>
                    </div>
                    <div class="flex items-start justify-end gap-3 max-w-[85%]">
                        <div
                            class="px-5 py-3 bg-slate-800 dark:bg-slate-700 rounded-2xl rounded-tr-none text-white text-sm leading-relaxed shadow-md border border-slate-700/50">
                            {{ msg.text }}
                        </div>
                        <div
                            class="w-8 h-8 rounded-full bg-gradient-to-tr from-slate-600 to-slate-400 flex items-center justify-center shrink-0 shadow-sm">
                            <app-icon name="user" [size]="16" class="text-white"></app-icon>
                        </div>
                    </div>
                </div>
                } @else {
                <!-- AI message bubble -->
                <div class="flex flex-col items-start gap-1.5 animate-in slide-in-from-left-4 duration-300">
                    <div class="flex items-center gap-2 px-1">
                        <div
                            class="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center shrink-0 shadow-sm">
                            <app-icon name="bot" [size]="16" class="text-white"></app-icon>
                        </div>
                        <span class="text-xs font-bold text-purple-600 dark:text-purple-400">RAG Assistant</span>
                        <span class="text-[10px] font-bold text-slate-400 uppercase tracking-tight">{{ msg.time
                            }}</span>
                    </div>
                    <div class="max-w-[90%] pl-11">
                        @if (msg.isLoading) {
                        <div
                            class="px-6 py-4 bg-white dark:bg-surface-2 border border-slate-100 dark:border-slate-800 rounded-2xl rounded-tl-none flex items-center gap-1.5 shadow-sm">
                            <span
                                class="w-2 h-2 rounded-full bg-purple-500 animate-bounce [animation-delay:-0.3s]"></span>
                            <span
                                class="w-2 h-2 rounded-full bg-purple-500 animate-bounce [animation-delay:-0.15s]"></span>
                            <span class="w-2 h-2 rounded-full bg-purple-500 animate-bounce"></span>
                        </div>
                        } @else {
                        <div
                            class="px-6 py-4 bg-white dark:bg-surface-2 border border-slate-100 dark:border-slate-800 rounded-2xl rounded-tl-none text-slate-800 dark:text-slate-100 text-sm leading-relaxed shadow-sm hover:shadow-md transition-shadow">
                            <markdown [data]="formatMessage(msg.text)" class="markdown-content"></markdown>

                            @if (msg.sources && msg.sources.length > 0) {
                            <div class="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800/50">
                                <span
                                    class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block mb-2">Sources:</span>
                                <div class="flex flex-wrap gap-2">
                                    @for (source of msg.sources; track source.id) {
                                    <div (click)="handleViewDocument({id: source.id})"
                                        class="group flex items-center gap-2 px-3 py-1.5 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/10 rounded-lg hover:border-purple-500/50 hover:bg-purple-50/5 dark:hover:bg-white/5 transition-all cursor-pointer">
                                        <app-icon name="fileText" [size]="12"
                                            class="text-slate-400 group-hover:text-purple-500"></app-icon>
                                        <span
                                            class="text-[11px] font-medium text-slate-600 dark:text-slate-300 group-hover:text-purple-600 dark:group-hover:text-white truncate max-w-[150px]">
                                            {{ source.name }}
                                        </span>
                                    </div>
                                    }
                                </div>
                            </div>
                            }
                        </div>
                        }
                    </div>
                </div>
                }
                }
            </div>

            <!-- Input Area (fixed at bottom) -->
            <div class="px-4 pt-2 pb-4 border-t border-slate-100 dark:border-slate-800 shrink-0">
                @if (!queryCollectionName()) {
                <p class="text-xs text-amber-500 mb-2 text-center">Please select a collection to start querying.</p>
                }
                <div
                    class="relative bg-slate-50 dark:bg-surface-1 rounded-2xl px-4 py-3 pr-14 border border-slate-200 dark:border-slate-800/60 focus-within:border-purple-500/50 transition-all min-h-[60px] flex items-center">
                    <textarea [ngModel]="queryText()" (ngModelChange)="queryText.set($event)"
                        (keydown.enter)="$event.preventDefault(); submitQuery()"
                        [disabled]="isQueryLoading() || !queryCollectionName()" placeholder="Ask Query" rows="2"
                        class="w-full bg-transparent border-none focus:ring-0 text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 resize-none text-sm p-0 outline-none disabled:opacity-50 disabled:cursor-not-allowed"></textarea>

                    <!-- Send Button with gradient border -->
                    <button (click)="submitQuery()"
                        [disabled]="isQueryLoading() || !queryText() || !queryCollectionName()"
                        class="absolute bottom-3 right-3 w-9 h-9 rounded-full border-2 border-l-purple-500 border-r-cyan-500 border-t-cyan-500 border-b-cyan-500 dark:border-l-pink-400 dark:border-r-sky-400 dark:border-t-sky-400 dark:border-b-sky-400 flex items-center justify-center hover:scale-110 active:scale-95 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100">
                        <app-icon name="arrowRight" [size]="18" class="text-purple-500 dark:text-pink-400"></app-icon>
                    </button>
                </div>
            </div>
        </div>
    </app-slide-panel>

    <!-- Audit Logs -->
    <div class="mt-8 pb-10">
        <app-audit-log-panel [entityType]="auditEntityTypes" title="Knowledge Base Audit Trails">
        </app-audit-log-panel>
    </div>


</div>